// src/hooks/useFileViewer.js
import { useState, useCallback } from "react";
import API_ENDPOINTS from "../services/apiEndpoints";
import { getAuthToken } from "../auth/auth";

/**
 * useFileViewer
 *
 * Manages the full file viewing flow — loading state, signed URL
 * fetching, error handling, and opening the file in a new tab.
 *
 * @param {string} userId - The ID of the user who owns the files
 *
 * @returns {{ handleViewFile: Function, loadingFiles: Object }}
 *
 * @example
 * const { handleViewFile, loadingFiles } = useFileViewer(formData.userId);
 * // Then per file:
 * handleViewFile(fileName, fileKey, category);
 */
function useFileViewer(userId) {
  const [loadingFiles, setLoadingFiles] = useState({});

  const handleViewFile = useCallback(
    async (fileName, fileKey, category) => {
      if (!fileName) return;

      setLoadingFiles((prev) => ({ ...prev, [fileKey]: true }));

      try {
        const token = await getAuthToken();

        if (!token) {
          throw new Error("Authentication token not found. Please log in again.");
        }

        const url = new URL(API_ENDPOINTS.FILE_STORAGE);
        url.searchParams.set("mode", "view");
        url.searchParams.set("category", category);
        url.searchParams.set("filename", fileName);
        url.searchParams.set("originalUserId", userId);

        const response = await fetch(url.toString(), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          const errorMessages = {
            401: "Authentication failed. Please log in again.",
            403: "Access denied. You don't have permission to view this file.",
            404: "File not found. It may have been deleted or moved."
          };
          throw new Error(errorMessages[response.status] || `Failed to fetch file viewing URL. Status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.viewURL) {
          throw new Error("Invalid response from server - no viewURL provided");
        }

        window.open(data.viewURL, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.error("[useFileViewer]", error);

        const message = error.name === "TypeError" && error.message.includes("Failed to fetch") ? "Network error: Unable to connect to the server." : error.message;

        // Return the error message so the page can display it however it wants
        return message;
      } finally {
        setLoadingFiles((prev) => ({ ...prev, [fileKey]: false }));
      }
    },
    [userId]
  );

  return { handleViewFile, loadingFiles };
}

export default useFileViewer;
